import { useState, FormEvent, useMemo, useEffect } from "react";
import Form from "react-bootstrap/Form";
import { ButtonWithSpinner } from "../../common/button-with-spinner/button-with-spinner";
import { resources } from "@constants/hardcoded-strings";
import { Company } from "@idw-types/data/reference-data/generic";
import { SelectOption } from "@idw-types/common";
import { SearchFormFilterRow } from "../../common/search-form-filter-row/search-form-filter-row";
import { TimeRangeTypes } from "@enums/time-range-types";
import { getTimeRange, getTimeRangeLabel } from "@utils/date-util";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { ButtonTypes } from "@enums/button-types";
import { MONTH_DAY_YEAR_INPUT_DATE_FORMAT } from "@constants/date-formats";
import { DashboardSearchFiltersState } from "@idw-types/state/dashboard-state";
import {
    mapEntityWithNameAndIdToSelectOption
} from "@utils/select-option-mapping-util";
import { PlaceholderSearchFormFilter } from "../../common/placeholder-search-form-filter/placeholder-search-form-filter";
import { sortStrings } from "@utils/string-util";
import { SelectFormGroup } from "../../common/select-form-group/select-form-group";
import { LabelValueContainer } from "../../common/label-value-container/label-value-container";
import { DateInputFormGroup } from "../../common/date-input-form-group/date-input-form-group";
import Button from "react-bootstrap/Button";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import styles from "./dashboard-search-form.less";

type DashboardSearchFormProps = {
    requestInProgress: boolean;
    isAdmin: boolean;
    userCompany: Company;
    companies: Array<Company>;
    searchFilters: DashboardSearchFiltersState;
    handleSearch: (searchFilters: DashboardSearchFiltersState) => void;
};

type DashboardSearchFormState = {
    selectedCompanyOption: SelectOption;
    selectedTimeRange: TimeRangeTypes;
    startDate: Date;
    endDate: Date;
};

export const DashboardSearchForm = (props: DashboardSearchFormProps) => {
    const maxDate = useMemo(() => new Date(), []);
    const minDate = useMemo(() => new Date(2015, 0, 1), []);
    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const {
        requestInProgress,
        isAdmin,
        userCompany,
        companies,
        searchFilters,
        handleSearch
    } = props;

    const getFilterStateFromSearchFilters = (searchFilters: DashboardSearchFiltersState): DashboardSearchFormState => {
        const selectedCompany = companies.find(company => searchFilters.companyId === company.id);

        return {
            selectedCompanyOption: selectedCompany
                ? mapEntityWithNameAndIdToSelectOption(selectedCompany)
                : { label: "All Organizations", value: null },
            selectedTimeRange: searchFilters.timeRangeType,
            startDate: searchFilters.startDate,
            endDate: searchFilters.endDate
        };
    };

    const [filterState, setFilterState] = useState<DashboardSearchFormState>(getFilterStateFromSearchFilters(searchFilters));

    useEffect(() => {
        setFilterState(getFilterStateFromSearchFilters(searchFilters));
    }, [searchFilters]);

    useEffect(() => {
        const company = companies.find(company => company.id === filterState.selectedCompanyOption?.value);
        handleSearch({
            companyId: company ? company.id : null,
            startDate: filterState.startDate,
            endDate: filterState.endDate,
            timeRangeType: filterState.selectedTimeRange
        });
    }, [filterState.selectedTimeRange, filterState.selectedCompanyOption?.value]);

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        const companyId = filterState.selectedCompanyOption?.value || null;
        handleSearch({
            startDate: filterState.startDate,
            endDate: filterState.endDate,
            timeRangeType: filterState.selectedTimeRange,
            companyId
        });
    };

    const handleUpdateCompanyOption = (newValue: SelectOption) => {
        setFilterState({
            ...filterState,
            selectedCompanyOption: newValue,
        });
    };

    const handleTimeRangeChange = (newTimeRange: TimeRangeTypes) => {
        if (newTimeRange === TimeRangeTypes.CUSTOM) {
            setFilterState({
                ...filterState,
                selectedTimeRange: newTimeRange
            });
        } else {
            setFilterState({
                ...filterState,
                selectedTimeRange: newTimeRange,
                ...getTimeRange(newTimeRange as TimeRangeTypes)
            });
        }
    };

    const handleStartDateChange = (newStartDate: Date) => {
        setFilterState({
            ...filterState,
            startDate: newStartDate
        });
    };

    const handleEndDateChange = (newEndDate: Date) => {
        setFilterState({
            ...filterState,
            endDate: newEndDate
        });
    };

    const timeRangeTypes = [
        TimeRangeTypes.PAST_TWENTY_FOUR_HOURS,
        TimeRangeTypes.PAST_SEVEN_DAYS,
        TimeRangeTypes.PAST_THIRTY_DAYS,
        TimeRangeTypes.PAST_NINETY_DAYS,
        TimeRangeTypes.CUSTOM
    ];

    const organizationOptions = useMemo(() => {
        const options = companies.filter(company => company.type === "RP")
            .map(company => ({ label: company.name, value: company.id }))
            .sort((a, b) => sortStrings(a.label, b.label));
        options.unshift({ label: "All Organizations", value: null });
        return options;
    }, [companies]);

    return (
        <Form className={styles.dashboardSearchForm} onSubmit={handleSubmit}>
            <SearchFormFilterRow>
                <PlaceholderSearchFormFilter />
                {isAdmin ? (
                    <SelectFormGroup
                        controlId={"dashboard-organization-select"}
                        label=""
                        className={styles.organizationSelectFormGroup}
                        ariaLabel="Organization"
                        automationId={"dashboard-organization-select"}
                        onChange={handleUpdateCompanyOption}
                        options={organizationOptions}
                        selectedOption={filterState.selectedCompanyOption}
                        disabled={false}
                        isRequired={false}
                        isSearchable={true}
                        isClearable={false}
                        labelWidth={LabelWidthTypes.EXTRA_SMALL}
                        layout={LayoutTypes.ROW}
                    />
                ) : (
                    <LabelValueContainer
                        controlId={"dashboard-organization-label-value"}
                        label=""
                        labelWidth={LabelWidthTypes.EXTRA_SMALL}
                        layout={LayoutTypes.ROW}
                        value={userCompany?.name}
                        labelIsBold={true}
                    />
                )}
                <PlaceholderSearchFormFilter />
            </SearchFormFilterRow>
            <SearchFormFilterRow>
                <ButtonGroup className={styles.timeRangeButtonGroup}>
                    {timeRangeTypes.map(trt => {
                        const isSelected = filterState.selectedTimeRange === trt;
                        return (
                            <Button
                                onClick={() => handleTimeRangeChange(trt)}
                                variant={isSelected ? "primary" : "outline-primary"}
                                key={`dashboard-time-range-${trt}`}
                                id={`dashboard-time-range-${trt.toLocaleLowerCase()}`}
                            >
                                {getTimeRangeLabel(trt)}
                            </Button>
                        );
                    })}
                </ButtonGroup>
            </SearchFormFilterRow>
            {filterState.selectedTimeRange === TimeRangeTypes.CUSTOM && (
                <SearchFormFilterRow>
                    <DateInputFormGroup
                        controlId={`dashboard-start-date-input`}
                        automationId={`dashboard-start-date-input`}
                        labelWidth={labelWidth}
                        label={resources.common.searchForm.filters.startDate.label}
                        ariaLabel={resources.common.searchForm.filters.startDate.label}
                        disabled={false}
                        isRequired={true}
                        layout={layout}
                        minDate={minDate}
                        maxDate={maxDate}
                        dateFormat={MONTH_DAY_YEAR_INPUT_DATE_FORMAT}
                        value={filterState.startDate}
                        onChange={handleStartDateChange}
                        shouldCloseOnSelect={true}
                        showMonthDropdown={true}
                        showYearDropdown={true}
                        showMonthYearPicker={false}
                        showTimeSelect={false}
                    />
                    <DateInputFormGroup
                        controlId={`dashboard-end-date-input`}
                        automationId={`dashboard-end-date-input`}
                        labelWidth={labelWidth}
                        label={resources.common.searchForm.filters.endDate.label}
                        ariaLabel={resources.common.searchForm.filters.endDate.label}
                        disabled={false}
                        isRequired={true}
                        layout={layout}
                        minDate={minDate}
                        maxDate={maxDate}
                        dateFormat={MONTH_DAY_YEAR_INPUT_DATE_FORMAT}
                        value={filterState.endDate}
                        onChange={handleEndDateChange}
                        shouldCloseOnSelect={true}
                        showMonthDropdown={true}
                        showYearDropdown={true}
                        showMonthYearPicker={false}
                        showTimeSelect={false}
                    />
                    <div className={styles.buttonDiv}>
                        <ButtonWithSpinner
                            id={'dashboard-custom-date-range-load-button'}
                            disabled={requestInProgress}
                            hideSpinner={false}
                            variant="primary"
                            type={ButtonTypes.SUBMIT}
                            label={resources.common.buttons.load.label}
                        />
                    </div>
                </SearchFormFilterRow>
            )}
        </Form>
    );
};